import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    AmountStepper,
    Button,
    Dialog,
    EmptyState,
    IconButton,
    KeyValueList,
    List,
    Money,
    QuickAmountChips,
} from '@/components/ui';
import APP_CONFIG from '@/config/appConfig';
import { playClickSound } from '@/utils/soundUtils';

interface GameProvider {
    id: string;
    name: string;
    icon: string;
}

interface OrderSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalAmount: number;
    providerAmounts: Map<string, number>;
    providers: GameProvider[];
    onIncrement: (providerId: string) => void;
    onDecrement: (providerId: string) => void;
    onRemoveItem: (providerId: string) => void;
    selectedProvider?: GameProvider | null;
    selectedAction?: 'deposit' | 'withdrawal' | null;
    onConfirm?: () => void;
    /**
     * Cosmos cart mode: when provided, rows render QuickAmountChips + AmountStepper
     * with absolute amounts instead of the legacy +/- controls.
     */
    onAmountChange?: (providerId: string, amount: number) => void;
    /** Per-provider max deposit (falls back to 10000). Only used with onAmountChange. */
    providerMaxAmounts?: Map<string, number>;
}

const OrderSummaryModal: React.FC<OrderSummaryModalProps> = ({
    isOpen,
    onClose,
    totalAmount,
    providerAmounts,
    providers,
    onIncrement,
    onDecrement,
    onRemoveItem,
    selectedProvider,
    selectedAction,
    onConfirm,
    onAmountChange,
    providerMaxAmounts,
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Check if there are any items in the order
    const cartEntries = Array.from(providerAmounts.entries()).filter(([_, amount]) => amount > 0);
    const hasItems = cartEntries.length > 0;
    const stepAmount = APP_CONFIG.jumpingEachClick || 5;

    const handleConfirmDeposit = () => {
        // Save provider amounts to sessionStorage
        const amountsArray = cartEntries.map(([providerId, amount]) => ({ providerId, amount }));

        if (amountsArray.length > 0) {
            const pendingData = {
                amounts: amountsArray,
                timestamp: Date.now(),
                source: 'confirm_deposit'
            };

            try {
                sessionStorage.setItem('pending_deposit_amounts', JSON.stringify(pendingData));
                console.log('💾 Saved pending deposit amounts to sessionStorage:', pendingData);
            } catch (error) {
                console.error('Failed to save to sessionStorage:', error);
            }
        }

        // Close popup
        onClose();

        // Call custom confirm handler if provided
        if (onConfirm) {
            onConfirm();
            return;
        }

        // Navigate to login page with provider and action info
        const params = new URLSearchParams();
        if (selectedProvider) {
            params.set('provider', String(selectedProvider.id));
            params.set('providerName', selectedProvider.name);
        }
        if (selectedAction) {
            params.set('action', selectedAction);
        }
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            size="md"
            closeLabel={t('common.close', 'Close')}
            title={t('orderSummary.title', 'Add Funds Cart')}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>
                        {t('common.close', 'Close')}
                    </Button>
                    <Button variant="primary" disabled={!hasItems} onClick={handleConfirmDeposit}>
                        {t('orderSummary.continueToCheckout', 'Continue to Checkout')}
                    </Button>
                </>
            }
        >
            {hasItems ? (
                <div className="flex flex-col gap-3">
                    <List inset>
                        {cartEntries.map(([providerId, amount]) => {
                            const provider = providers.find(p => p.id === providerId);
                            if (!provider) return null;
                            const maxAmount = providerMaxAmounts?.get(providerId) ?? 10000;

                            return (
                                <div key={providerId} role="listitem" className="flex flex-col gap-2 px-3.5 py-3">
                                    {/* Row header: icon + name + amount + remove */}
                                    <div className="flex w-full items-center justify-between gap-2">
                                        <div className="flex min-w-0 flex-1 items-center gap-2.5">
                                            <span className="inline-flex size-8 shrink-0 overflow-hidden rounded-control border border-edge-subtle bg-inset">
                                                <img
                                                    src={provider.icon}
                                                    alt={provider.name}
                                                    className="size-full object-cover"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = '/icons/egame.jpg';
                                                    }}
                                                />
                                            </span>
                                            <p className="m-0 min-w-0 truncate font-ui text-small font-semibold text-primary">
                                                {provider.name}
                                            </p>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-1.5">
                                            {onAmountChange ? (
                                                <Money amount={Math.round(amount)} direction="in" />
                                            ) : (
                                                /* Legacy +/- controls (callers without onAmountChange) */
                                                <div className="flex items-center gap-1">
                                                    <IconButton
                                                        size="sm"
                                                        icon={<Minus />}
                                                        label={t('orderSummary.decrease', 'Decrease amount')}
                                                        disabled={amount <= 0}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDecrement(providerId);
                                                        }}
                                                    />
                                                    <span className="inline-flex min-w-18 justify-center">
                                                        <Money amount={Math.round(amount)} direction="in" size="small" />
                                                    </span>
                                                    <IconButton
                                                        size="sm"
                                                        icon={<Plus />}
                                                        label={t('orderSummary.increase', 'Increase amount')}
                                                        disabled={amount >= 10000}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onIncrement(providerId);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <IconButton
                                                size="sm"
                                                variant="ghost"
                                                icon={<Trash2 />}
                                                label={t('orderSummary.remove', 'Remove {{name}} from cart', { name: provider.name })}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRemoveItem(providerId);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Per-row amount editing: quick chips + stepper */}
                                    {onAmountChange && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <QuickAmountChips
                                                value={amount}
                                                onSelect={(amt) => {
                                                    playClickSound();
                                                    onAmountChange(providerId, Math.min(amt, maxAmount));
                                                }}
                                            />
                                            <AmountStepper
                                                value={amount}
                                                onChange={(next) => onAmountChange(providerId, next)}
                                                step={stepAmount}
                                                min={0}
                                                max={maxAmount}
                                                format={(v) => `$${Math.round(v).toFixed(2)}`}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </List>
                    <KeyValueList
                        items={[
                            {
                                label: t('orderSummary.total', 'Total Deposit'),
                                value: <Money amount={Math.round(totalAmount)} direction="in" size="title" />,
                                emphasis: true,
                            },
                        ]}
                    />
                </div>
            ) : (
                <EmptyState
                    icon={<Wallet />}
                    title={t('orderSummary.emptyTitle', 'No games selected')}
                    description={t('orderSummary.emptyHint', 'Go back and select some games to fund your adventure.')}
                />
            )}
        </Dialog>
    );
};

export default OrderSummaryModal;
